<template>
  <div class="profile-setup-container">
    <div class="profile-setup-header">
      <h1>{{ t('profileSetup.title') }}</h1>
      <p class="profile-description">
        {{ t('profileSetup.description') }}
      </p>
      <div class="avatar-top">
        <img
          v-if="profileData.avatarUrl && isValidUrl(profileData.avatarUrl)"
          :src="profileData.avatarUrl"
          alt="Profile avatar"
          class="avatar-top-image"
        >
        <div
          v-else
          class="avatar-top-placeholder"
          aria-label="No avatar"
        >
          {{ t('profileSetup.noAvatar') }}
        </div>
        <button
          type="button"
          class="btn-secondary change-avatar-btn"
          @click="openAvatarModal"
        >
          {{ t('profileSetup.changeAvatar') }}
        </button>
      </div>
    </div>

    <div
      class="profile-setup-content"
      :class="{ 'is-loading': isLoading }"
    >
      <div
        v-if="isLoading"
        class="loading-overlay"
        aria-hidden="true"
      >
        <span class="loader" />
      </div>
      <form @submit.prevent="saveProfile">
        <!-- Avatar modal -->
        <div
          v-if="showAvatarModal"
          class="modal-backdrop"
          @click.self="closeAvatarModal"
        >
          <div
            class="modal-card"
            role="dialog"
            aria-modal="true"
          >
            <div class="modal-header">
              {{ t('profileSetup.changeAvatar') }}
            </div>
            <div class="modal-body">
              <label for="avatarUrlInput">{{ t('profileSetup.avatarUrl') }}</label>
              <input
                id="avatarUrlInput"
                type="url"
                v-model="tempAvatarUrl"
                :placeholder="t('profileSetup.avatarUrlPlaceholder')"
              >
              <div
                class="avatar-preview"
                v-if="tempAvatarUrl && isValidUrl(tempAvatarUrl)"
              >
                <img
                  :src="tempAvatarUrl"
                  alt="Avatar preview"
                  class="avatar-image"
                >
              </div>
            </div>
            <div class="modal-actions">
              <button
                type="button"
                class="btn-secondary"
                @click="closeAvatarModal"
              >
                {{ t('common.cancel') }}
              </button>
              <button
                type="button"
                class="btn-primary"
                @click="confirmAvatarModal"
              >
                {{ t('common.save') }}
              </button>
            </div>
          </div>
        </div>

        <div
          v-for="field in profile_fields"
          :key="field.key"
        >
          <div
            v-if="field.key === 'emails'"
            class="form-group"
            :class="field.required ? 'required-field' : ''"
          >
            <label for="field.key">{{ t('profileSetup.' + field.label) }}</label>
            <div class="secondary-emails-container">
              <template v-if="profileData?.emails && profileData.emails.length > 0">
                <div 
                  v-for="(email, index) in profileData.emails" 
                  :key="index" 
                  class="secondary-email-row"
                >
                  <input
                    type="email"
                    v-model="profileData.emails[index]"
                    @input="clearError"
                    :placeholder="t('profileSetup.' + field.label + 'Placeholder')"
                    class="secondary-email-input"
                  >
                  <button 
                    type="button" 
                    @click="removeSecondaryEmail(index)"
                    class="remove-email-btn"
                    :disabled="profileData.emails.length <= 1"
                  >
                    ×
                  </button>
                </div>
              </template>
              <button 
                type="button" 
                @click="addSecondaryEmail"
                class="add-email-btn"
                :disabled="profileData?.emails?.length >= 5"
              >
                {{ t('profileSetup.addSecondaryEmail') }}
              </button>
            </div>
            <small
              v-if="t('profileSetup.secondaryEmailsHelp', '')"
              class="field-help"
            >{{ t('profileSetup.secondaryEmailsHelp', '') }}</small>
          </div>
          <div
            v-else
            class="form-group"
            :class="field.required ? 'required-field' : ''"
          >
            <label for="field.key">{{ t('profileSetup.' + field.label, field.label) }}</label>
            <input
              :type="field.type"
              :id="field.key"
              v-model="profileData[field.key]"
              @input="clearError"
              :placeholder="t('profileSetup.' + field.label + 'Placeholder')"
              :required="field.required || false"              
            >
            <small
              v-if="t('profileSetup.' + field.label + 'Help', '')"
              class="field-help"
            >{{ t('profileSetup.' + field.label + 'Help', '') }}</small>
          </div>
        </div>
        <div
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </div>

        <div
          v-if="successMessage"
          class="success-message"
        >
          <span class="success-icon">✓</span>
          {{ successMessage }}
        </div>

        <div class="form-actions">
          <button
            type="button"
            @click="gotoNextProfilePage"
            class="btn-secondary"
            v-if="hasPendingRegistrationForCurrentService && false"
          >
            {{ t('profileSetup.nextPage') }}
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="isSubmitting || isLoading"
          >
            <span
              v-if="isSubmitting"
              class="btn-spinner"
            />
            <template v-if="hasPendingRegistrationForCurrentService">
              {{ isSubmitting ? t('profileSetup.saving') : t('profileSetup.save') }}
            </template>
            <template v-else>
              {{ isSubmitting ? t('profileSetup.updating') : t('profileSetup.update') }}
            </template>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { r7eBaseApp } from '@r7e/shared';

const r7eApp = r7eBaseApp.getMainApp();
const router = useRouter();
const { t } = useI18n();

// Optional destination to go to after successful save
const props = defineProps({
  redirectTo: {
    type: String,
    default: ''
  },
  callback: {
    type: Function,
    default: () => {}
  }
});

// Form state
const isLoading = ref(true);
const hasPendingRegistrationForCurrentService = ref(r7eApp.hasPendingRegistrationForCurrentService());
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const showAvatarModal = ref(false);
const tempAvatarUrl = ref('');

// Profile data
const profileData = ref({ });

const profile_fields = r7eApp.getProfileFieldsSettings();

// Methods
const clearError = () => {
  errorMessage.value = '';
  successMessage.value = '';
};

const validateForm = () => {
  // Clear previous errors
  errorMessage.value = '';
  
  // Required field validation
  // if (!profileData.value?.affiliation?.trim()) {
  //   errorMessage.value = t('profileSetup.errors.affiliationRequired');
  //   return false;
  // }

  // Verification for required fields and optional fields with validation
  for (const field of profile_fields) {
    let value = profileData.value[field.key];
    if (typeof value === 'string') {
      value = value.trim();
    }
    if (field.required) {
      if (!value) {
        errorMessage.value = t('profileSetup.errors.requiredField');
        return false;
      }
    }
    if (value &&field.validation && !field.validation(value)) {
      errorMessage.value = t('profileSetup.errors.' + field.errorLabel);
      return false;
    }
  }

  
  // Secondary emails validation
  const validSecondaryEmails = profileData.value?.emails?.filter(email => email.trim());
  if (validSecondaryEmails && validSecondaryEmails.length > 5) {
    errorMessage.value = t('profileSetup.errors.tooManySecondaryEmails');
    return false;
  }
  if (validSecondaryEmails) {
    for (const email of validSecondaryEmails) {
      if (!isValidEmail(email)) {
          errorMessage.value = t('profileSetup.errors.invalidSecondaryEmail');
          return false;
        }
    }
  }

  // Avatar URL validation
  if (profileData.value?.avatarUrl && !isValidUrl(profileData.value.avatarUrl)) {
    errorMessage.value = t('profileSetup.errors.invalidAvatarUrl');
    return false;
  }
  
  return true;
};

const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const isValidEmail = (email) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

const addSecondaryEmail = () => {
  if (!profileData.value?.emails) {
    profileData.value.emails = [];
  }
  if (profileData.value?.emails?.length < 5) {
    profileData.value.emails.push('');
  }
};

const removeSecondaryEmail = (index) => {
  if (profileData.value?.emails?.length > 1) {
    profileData.value.emails.splice(index, 1);
  }
};

const openAvatarModal = () => {
  tempAvatarUrl.value = profileData.value.avatarUrl || '';
  showAvatarModal.value = true;
};

const closeAvatarModal = () => {
  showAvatarModal.value = false;
};

const confirmAvatarModal = () => {
  if (tempAvatarUrl.value && !isValidUrl(tempAvatarUrl.value)) {
    errorMessage.value = t('profileSetup.errors.invalidAvatarUrl');
    return;
  }
  profileData.value.avatarUrl = tempAvatarUrl.value.trim();
  showAvatarModal.value = false;
};

const saveProfile = async () => {
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';
  
  try {
    // Prepare the profile update payload
    const prof = {}
    for (const field of profile_fields) {
      if (profileData.value[field.key]) {
        let val = profileData.value[field.key]
        if (typeof val === 'string' && typeof val.trim === 'function') {
          val = val.trim();
        }
        prof[field.key] = val;
      }
    }

    if (profileData.value?.avatarUrl) {
      prof.avatar_url = profileData.value.avatarUrl;
    }
    
    // Handle secondary emails - filter out empty ones
    const validSecondaryEmails = profileData.value?.emails
      ?.filter(email => email.trim())
      ?.map(email => email.trim());
    
    if (validSecondaryEmails && validSecondaryEmails.length > 0) {
      prof.emails = validSecondaryEmails;
    }

    const profilePayload = { profile: prof };
    
    // Call the backend to update profile
    const response = await r7eApp.updateProfile(profilePayload);
    
    if (response && response.profile) { 
      successMessage.value = t('profileSetup.success.saved');
      await r7eApp.requestAccountRefresh();
      // Redirect after a short delay to show success message
      // console.log('props.redirectTo', props.redirectTo);
      // console.log('props.callback', props.callback);
      if (props.callback) {
        props.callback();
      }
      setTimeout(() => {
        if (props.redirectTo) {
          router.push(props.redirectTo);
        } else {
          router.push('/');
        }
      }, 1500);
    } else {
      errorMessage.value = t('profileSetup.errors.saveFailed');
    }
       
  } catch (error) {
    console.error('updateProfile Error', { error: error, response: error.response });
    
    // Extract error message from various possible response formats
    let errorMsg = '';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.error) {
        errorMsg = errorData.error;
      } else if (errorData.message) {
        errorMsg = errorData.message;
      } else if (errorData.detail) {
        errorMsg = errorData.detail;
      } else if (typeof errorData === 'string') {
        errorMsg = errorData;
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        errorMsg = errorData.errors.join(', ');
      } else {
        errorMsg = `Profile update failed: ${JSON.stringify(errorData)}`;
      }
    } else if (error.message) {
      errorMsg = error.message;
    } else {
      errorMsg = t('profileSetup.errors.saveFailed');
    }
    
    errorMessage.value = errorMsg;
  } finally {
    isSubmitting.value = false;
  }
};

const nextProfilePage = () => {
  const setupPages = r7eApp.setupPages();
  const currentPage = router.currentRoute.value.path;
  const currentIndex = setupPages.findIndex(page => page === currentPage);
  const nextPage = currentIndex !== -1 && currentIndex + 1 < setupPages.length
    ? setupPages[currentIndex + 1]
    : null;

  // console.log('currentPage', currentPage);
  // console.log('nextPage', nextPage);
  return nextPage;
}

const gotoNextProfilePage = (dft = '/info') => {
  const nextPage = nextProfilePage();
  if (nextPage) {
    router.push(nextPage);
  } else {
    router.push(dft);
  }  
};

// Load existing profile data on component mount
onMounted(async () => {
  // console.log('ProfileSetupView, onMounted, redirectTo', props.redirectTo);
  // console.log('ProfileSetupView, onMounted, callback', props.callback);

  try {
    // Try to load existing profile data
    const existingProfile = await r7eApp.getProfile();
    
    if (existingProfile && existingProfile.profile) {
      const prof = existingProfile.profile;
      for (const field of profile_fields) {
        if (prof[field.key]) {
          profileData.value[field.key] = prof[field.key];
        }
      }
    }
    if (existingProfile?.profile?.avatar_url) {
      profileData.value.avatarUrl = existingProfile.profile.avatar_url;
    }
  } catch (error) {
    console.error('No existing profile found or error loading profile:', error);
    // Continue with empty form - this is normal for new users
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.avatar-top {
  margin-top: 16px;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
}

.avatar-top-image {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--color-border);
  background-color: #f2f2f2;
}

.avatar-top-placeholder {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  background-color: #f2f2f2;
  border: 2px solid var(--color-border);
  font-size: 0.9em;
}

.change-avatar-btn {
  height: 40px;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: #fff;
  width: min(520px, 92vw);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  overflow: hidden;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  font-weight: bold;
}

.modal-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-actions {
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid var(--color-border);
}

.avatar-preview {
  margin-top: 10px;
}

.avatar-image {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--color-border);
}

.profile-setup-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--color-frame-bg);
  min-height: calc(100vh - 100px);
}

.profile-setup-header {
  text-align: center;
  margin-bottom: 40px;
}

.profile-setup-header h1 {
  color: var(--color-text-dark);
  margin-bottom: 15px;
}

.profile-description {
  color: var(--color-text);
  font-size: 1.1em;
  margin: 0;
}

.profile-setup-content {
  background-color: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: relative;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: var(--color-text-dark);
}
.form-group.required-field label::after {
  content: '*';
  color: #dc3545;
  margin-left: 4px;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: var(--color-safe);
}

.field-help {
  display: block;
  margin-top: 5px;
  font-size: 0.85em;
  color: var(--color-text);
  font-style: italic;
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.success-message .success-icon {
  font-size: 1.2em;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 40px;
  gap: 15px;
}

.btn-primary {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-safe);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-text-dark);
}

.btn-primary:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Loading state overlay */
.profile-setup-content.is-loading {
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loader {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--color-safe);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Secondary emails styles */
.secondary-emails-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.secondary-email-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.secondary-email-input {
  flex: 1;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
}

.secondary-email-input:focus {
  outline: none;
  border-color: var(--color-safe);
}

.remove-email-btn {
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-border);
  background-color: #f8f9fa;
  color: #dc3545;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  transition: all 0.2s;
}

.remove-email-btn:hover:not(:disabled) {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.remove-email-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-email-btn {
  padding: 8px 16px;
  border: 1px solid var(--color-safe);
  background-color: transparent;
  color: var(--color-safe);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.2s;
  align-self: flex-start;
}

.add-email-btn:hover:not(:disabled) {
  background-color: var(--color-safe);
  color: white;
}

.add-email-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: var(--color-border);
  color: var(--color-border);
}

/* Responsive design */
@media (max-width: 768px) {
  .profile-setup-container {
    padding: 10px;
  }
  
  .profile-setup-content {
    padding: 20px;
  }
  
  .secondary-email-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .remove-email-btn {
    align-self: flex-end;
    margin-top: 5px;
  }
}

@media (max-width: 480px) {
  .profile-setup-header h1 {
    font-size: 1.5em;
  }
  
  .profile-description {
    font-size: 1em;
  }
  
  .form-group input {
    padding: 10px;
  }
  
  .btn-primary {
    padding: 10px 20px;
  }
}
</style>